import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Resume.css';
import axios from 'axios';
import { useAuth } from './context/AuthContext';

function Resume() {
  const { user } = useAuth(); // ✅ FIX
  const navigate = useNavigate();

  const userid = user?.userid; // ✅ SAFE ACCESS

  const [resumeData, setResumeData] = useState({
    personalInfo: {
      name: '',
      headline: '',
      location: '',
      email: '',
      phone: '',
      website: '',
      linkedin: '',
      github: '',
    },
    summary: '',
    experience: [],
    education: [],
    skills: [],
    projects: [],
    certifications: [],
    languages: [],
  });

  // ✅ Load saved resume safely
  useEffect(() => {
    if (!userid) return;

    const savedResume = localStorage.getItem(`resume_${userid}`);
    if (savedResume) {
      setResumeData(JSON.parse(savedResume));
    }
  }, [userid]);

  const saveResume = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("Please login again");
        return;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/resume/save`,
        resumeData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.status === 200) {
        localStorage.setItem(`resume_${userid}`, JSON.stringify(resumeData));
        alert("Resume saved successfully");
        navigate("/resume/${userid}");
      }
    } catch (err) {
      console.error("Resume save error:", err.response?.data || err.message);
      alert("Failed to save resume");
    }
  };

  const handlePersonalInfoChange = (field, value) => {
    setResumeData(prev => ({
      ...prev,
      personalInfo: {
        ...prev.personalInfo,
        [field]: value
      }
    }));
  };

  const handleArrayItemChange = (section, index, field, value) => {
    setResumeData(prev => ({
      ...prev,
      [section]: prev[section].map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const addArrayItem = (section, template) => {
    setResumeData(prev => ({
      ...prev,
      [section]: [...prev[section], { ...template }]
    }));
  };

  const removeArrayItem = (section, index) => {
    setResumeData(prev => ({
      ...prev,
      [section]: prev[section].filter((_, i) => i !== index)
    }));
  };

  const handleSkillChange = (index, value) => {
    handleArrayItemChange('skills', index, 'name', value);
  };

  const addSkill = () => {
    addArrayItem('skills', { name: '', category: 'Technical' });
  };

  const removeSkill = (index) => {
    removeArrayItem('skills', index);
  };

  const viewProfile = () => {
    // Save resume data to profile format
    const profileData = {
      id: userid,
      name: resumeData.personalInfo.name,
      headline: resumeData.personalInfo.headline,
      location: resumeData.personalInfo.location,
      connections: 0,
      followers: 0,
      profileViews: 0,
      about: resumeData.summary,
      contactInfo: {
        email: resumeData.personalInfo.email,
        website: resumeData.personalInfo.website,
        phone: resumeData.personalInfo.phone
      },
      experience: resumeData.experience,
      education: resumeData.education,
      skills: resumeData.skills,
      recommendations: [],
      achievements: resumeData.certifications
    };
    
    if (!userid) return;
    navigate(`/profile/${userid}`);
  };

  // THE REST OF YOUR JSX REMAINS EXACTLY THE SAME
  return (
    <div className="resume-container">
      <div className="resume-header">
        <h1>Resume Builder</h1>
        <div className="resume-actions">
          <button className="btn-save" onClick={saveResume}>
            💾 Save Resume
          </button>
          <button className="btn-view" onClick={viewProfile}>
            👁️ View Profile
          </button>
        </div>
      </div>
      <div className="resume-sections">
        {/* Personal Information */}
        <ResumeSection title="Personal Information">
          <div className="form-grid">
            <div className="resumeform-group">
              <label>Full Name *</label>
              <input
                type="text"
                value={resumeData.personalInfo.name}
                onChange={(e) => handlePersonalInfoChange('name', e.target.value)}
                placeholder="John Doe"
              />
            </div>
            <div className="resumeform-group">
              <label>Professional Headline</label>
              <input
                type="text"
                value={resumeData.personalInfo.headline}
                onChange={(e) => handlePersonalInfoChange('headline', e.target.value)}
                placeholder="Frontend Developer"
              />
            </div>
            <div className="resumeform-group">
              <label>Location</label>
              <input
                type="text"
                value={resumeData.personalInfo.location}
                onChange={(e) => handlePersonalInfoChange('location', e.target.value)}
                placeholder="San Francisco, CA"
              />
            </div>
            <div className="resumeform-group">
              <label>Email *</label>
              <input
                type="email"
                value={resumeData.personalInfo.email}
                onChange={(e) => handlePersonalInfoChange('email', e.target.value)}
                placeholder="john@example.com"
              />
            </div>
            <div className="resumeform-group">
              <label>Phone</label>
              <input
                type="tel"
                value={resumeData.personalInfo.phone}
                onChange={(e) => handlePersonalInfoChange('phone', e.target.value)}
                placeholder="(123) 456-7890"
              />
            </div>
            <div className="resumeform-group">
              <label>Website</label>
              <input
                type="url"
                value={resumeData.personalInfo.website}
                onChange={(e) => handlePersonalInfoChange('website', e.target.value)}
                placeholder="https://yourwebsite.com"
              />
            </div>
            <div className="resumeform-group">
              <label>LinkedIn</label>
              <input
                type="url"
                value={resumeData.personalInfo.linkedin}
                onChange={(e) => handlePersonalInfoChange('linkedin', e.target.value)}
                placeholder="https://linkedin.com/in/username"
              />
            </div>
            <div className="resumeform-group">
              <label>GitHub</label>
              <input
                type="url"
                value={resumeData.personalInfo.github}
                onChange={(e) => handlePersonalInfoChange('github', e.target.value)}
                placeholder="https://github.com/username"
              />
            </div>
          </div>
        </ResumeSection>
        {/* Professional Summary */}
        <ResumeSection title="Professional Summary">
          <div className="resumeform-group">
            <textarea
              value={resumeData.summary}
              onChange={(e) => setResumeData(prev => ({ ...prev, summary: e.target.value }))}
              placeholder="Write a brief summary of your professional background, skills, and career goals..."
              rows="6"
            />
          </div>
        </ResumeSection>
        {/* Work Experience */}
        <ResumeSection 
          title="Work Experience"
          onAdd={() => addArrayItem('experience', {
            title: '',
            company: '',
            location: '',
            startDate: '',
            endDate: '',
            current: false,
            description: '',
            skills: []
          })}
        >
          {resumeData.experience.map((exp, index) => (
            <div key={index} className="section-item">
              <div className="item-header">
                <h4>Experience #{index + 1}</h4>
                <button 
                  className="btn-remove"
                  onClick={() => removeArrayItem('experience', index)}
                >
                  Remove
                </button>
              </div>
              <div className="form-grid">
                <div className="resumeform-group">
                  <label>Job Title *</label>
                  <input
                    type="text"
                    value={exp.title}
                    onChange={(e) => handleArrayItemChange('experience', index, 'title', e.target.value)}
                    placeholder="Senior Frontend Developer"
                  />
                </div>
                <div className="resumeform-group">
                  <label>Company *</label>
                  <input
                    type="text"
                    value={exp.company}
                    onChange={(e) => handleArrayItemChange('experience', index, 'company', e.target.value)}
                    placeholder="TechCorp"
                  />
                </div>
                <div className="resumeform-group">
                  <label>Location</label>
                  <input
                    type="text"
                    value={exp.location}
                    onChange={(e) => handleArrayItemChange('experience', index, 'location', e.target.value)}
                    placeholder="San Francisco, CA"
                  />
                </div>
                <div className="resumeform-group">
                  <label>Start Date</label>
                  <input
                    type="text"
                    value={exp.startDate}
                    onChange={(e) => handleArrayItemChange('experience', index, 'startDate', e.target.value)}
                    placeholder="Mar 2021"
                  />
                </div>
                <div className="resumeform-group">
                  <label>End Date</label>
                  <input
                    type="text"
                    value={exp.endDate}
                    onChange={(e) => handleArrayItemChange('experience', index, 'endDate', e.target.value)}
                    placeholder="Present"
                    disabled={exp.current}
                  />
                </div>
                <div className="resumeform-group full-width">
                  <label>
                    <input
                      type="checkbox"
                      checked={exp.current}
                      onChange={(e) => handleArrayItemChange('experience', index, 'current', e.target.checked)}
                    />
                    Currently working here
                  </label>
                </div>
                <div className="resumeform-group full-width">
                  <label>Description</label>
                  <textarea
                    value={exp.description}
                    onChange={(e) => handleArrayItemChange('experience', index, 'description', e.target.value)}
                    placeholder="Describe your responsibilities and achievements..."
                    rows="4"
                  />
                </div>
              </div>
            </div>
          ))}
          {resumeData.experience.length === 0 && (
            <p className="empty-message">No experience added yet. Click "Add Experience" to get started.</p>
          )}
        </ResumeSection>
        {/* Education */}
        <ResumeSection 
          title="Education"
          onAdd={() => addArrayItem('education', {
            school: '',
            degree: '',
            field: '',
            startDate: '',
            endDate: '',
            gpa: '',
            description: ''
          })}
        >
          {resumeData.education.map((edu, index) => (
            <div key={index} className="section-item">
              <div className="item-header">
                <h4>Education #{index + 1}</h4>
                <button 
                  className="btn-remove"
                  onClick={() => removeArrayItem('education', index)}
                >
                  Remove
                </button>
              </div>
              <div className="form-grid">
                <div className="resumeform-group">
                  <label>School/University *</label>
                  <input
                    type="text"
                    value={edu.school}
                    onChange={(e) => handleArrayItemChange('education', index, 'school', e.target.value)}
                    placeholder="Stanford University"
                  />
                </div>
                <div className="resumeform-group">
                  <label>Degree *</label>
                  <input
                    type="text"
                    value={edu.degree}
                    onChange={(e) => handleArrayItemChange('education', index, 'degree', e.target.value)}
                    placeholder="Bachelor of Science"
                  />
                </div>
                <div className="resumeform-group">
                  <label>Field of Study</label>
                  <input
                    type="text"
                    value={edu.field}
                    onChange={(e) => handleArrayItemChange('education', index, 'field', e.target.value)}
                    placeholder="Computer Science"
                  />
                </div>
                <div className="resumeform-group">
                  <label>Start Date</label>
                  <input
                    type="text"
                    value={edu.startDate}
                    onChange={(e) => handleArrayItemChange('education', index, 'startDate', e.target.value)}
                    placeholder="2017"
                  />
                </div>
                <div className="resumeform-group">
                  <label>End Date</label>
                  <input
                    type="text"
                    value={edu.endDate}
                    onChange={(e) => handleArrayItemChange('education', index, 'endDate', e.target.value)}
                    placeholder="2021"
                  />
                </div>
                <div className="resumeform-group">
                  <label>GPA</label>
                  <input
                    type="text"
                    value={edu.gpa}
                    onChange={(e) => handleArrayItemChange('education', index, 'gpa', e.target.value)}
                    placeholder="3.8/4.0"
                  />
                </div>
                <div className="resumeform-group full-width">
                  <label>Description</label>
                  <textarea
                    value={edu.description}
                    onChange={(e) => handleArrayItemChange('education', index, 'description', e.target.value)}
                    placeholder="Add details about your studies, achievements, or activities..."
                    rows="3"
                  />
                </div>
              </div>
            </div>
          ))}
          {resumeData.education.length === 0 && (
            <p className="empty-message">No education added yet. Click "Add Education" to get started.</p>
          )}
        </ResumeSection>
        {/* Skills */}
        <ResumeSection 
          title="Skills"
          onAdd={addSkill}
        >
          <div className="skills-container">
            {resumeData.skills.map((skill, index) => (
              <div key={index} className="skill-input-row">
                <input
                  type="text"
                  value={skill.name}
                  onChange={(e) => handleSkillChange(index, e.target.value)}
                  placeholder="e.g., React, JavaScript, Python"
                  className="skill-input"
                />
                <button 
                  className="btn-remove-small"
                  onClick={() => removeSkill(index)}
                >
                  ×
                </button>
              </div>
            ))}
            {resumeData.skills.length === 0 && (
              <p className="empty-message">No skills added yet. Click "Add Skill" to get started.</p>
            )}
          </div>
        </ResumeSection>
        {/* Additional Sections - Projects */}
        <ResumeSection 
          title="Projects"
          onAdd={() => addArrayItem('projects', {
            name: '',
            description: '',
            technologies: '',
            link: '',
            date: ''
          })}
        >
          {resumeData.projects.map((project, index) => (
            <div key={index} className="section-item">
              <div className="item-header">
                <h4>Project #{index + 1}</h4>
                <button 
                  className="btn-remove"
                  onClick={() => removeArrayItem('projects', index)}
                >
                  Remove
                </button>
              </div>
              <div className="form-grid">
                <div className="resumeform-group">
                  <label>Project Name *</label>
                  <input
                    type="text"
                    value={project.name}
                    onChange={(e) => handleArrayItemChange('projects', index, 'name', e.target.value)}
                    placeholder="E-commerce Platform"
                  />
                </div>
                <div className="resumeform-group">
                  <label>Technologies</label>
                  <input
                    type="text"
                    value={project.technologies}
                    onChange={(e) => handleArrayItemChange('projects', index, 'technologies', e.target.value)}
                    placeholder="React, Node.js, MongoDB"
                  />
                </div>
                <div className="resumeform-group full-width">
                  <label>Description</label>
                  <textarea
                    value={project.description}
                    onChange={(e) => handleArrayItemChange('projects', index, 'description', e.target.value)}
                    placeholder="Describe the project and your role..."
                    rows="3"
                  />
                </div>
              </div>
            </div>
          ))}
        </ResumeSection>
        {/* Certifications */}
        <ResumeSection 
          title="Certifications"
          onAdd={() => addArrayItem('certifications', {
            name: '',
            issuer: '',
            date: '',
            credentialId: ''
          })}
        >
          {resumeData.certifications.map((cert, index) => (
            <div key={index} className="section-item">
              <div className="item-header">
                <h4>Certification #{index + 1}</h4>
                <button 
                  className="btn-remove"
                  onClick={() => removeArrayItem('certifications', index)}
                >
                  Remove
                </button>
              </div>
              <div className="form-grid">
                <div className="resumeform-group">
                  <label>Certification Name *</label>
                  <input
                    type="text"
                    value={cert.name}
                    onChange={(e) => handleArrayItemChange('certifications', index, 'name', e.target.value)}
                    placeholder="AWS Certified Solutions Architect"
                  />
                </div>
                <div className="resumeform-group">
                  <label>Issuing Organization</label>
                  <input
                    type="text"
                    value={cert.issuer}
                    onChange={(e) => handleArrayItemChange('certifications', index, 'issuer', e.target.value)}
                    placeholder="Amazon Web Services"
                  />
                </div>
                <div className="resumeform-group">
                  <label>Date Obtained</label>
                  <input
                    type="text"
                    value={cert.date}
                    onChange={(e) => handleArrayItemChange('certifications', index, 'date', e.target.value)}
                    placeholder="Jan 2023"
                  />
                </div>
              </div>
            </div>
          ))}
        </ResumeSection>
      </div>
    </div>
  );
}

function ResumeSection({ title, children, onAdd }) {
  return (
    <div className="resume-section">
      <div className="section-header">
        <h3>{title}</h3>
        {onAdd && (
          <button className="btn-add" onClick={onAdd}>
            + Add {title}
          </button>
        )}
      </div>
      <div className="section-content">
        {children}
      </div>
    </div>
  );
}

export default Resume;